import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import 'src/styles/tour.css';
import { useDispatch, useSelector } from 'react-redux';
import {
  setRunning,
  setCurrentTour,
  setTourSettings,
  setError,
} from 'src/redux/slices/tour';
import useAuth from 'src/hooks/useAuth';
import { getDiscriminators } from 'src/utils/permissions';
import isOwnerImpersonating from 'src/utils/adminImpersonation';
import {
  clearTourSessionState,
  getDefaultTourSessionState,
  getTourSessionState,
  setTourSessionState,
  TOUR_HIDE_PROMPT_SKIP_THRESHOLD,
} from 'src/hooks/tourSessionState';
import { clearTourLocalCache, mergeTourLocalCache } from 'src/hooks/tourCache';
import * as tourAPI from 'src/backend/tour';
import { useTranslation } from 'react-i18next';
import { useBrandText } from 'src/contexts/BrandContext';

const TourContext = createContext(null);

export const useTourContext = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext must be used within a TourProvider');
  }
  return context;
};

const TourProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const getBrandText = useBrandText();
  const { user } = useAuth();
  const isImpersonating = isOwnerImpersonating(user);
  const driverRef = useRef(null);
  const onCompleteRef = useRef(null);
  const onCloseRef = useRef(null);
  const currentStepIndexRef = useRef(0);
  const lastPromptedSkipCountRef = useRef(0);
  const { isRunning, currentTour, settings } = useSelector(
    (state) => state.tour
  );
  const areToursHidden = settings?.areToursHidden ?? false;
  const isTourSettingsLoaded = settings?.isLoaded ?? false;
  const [tourSessionState, setTourSessionStateState] = useState(
    getDefaultTourSessionState()
  );
  const [hideAllToursModalOpen, setHideAllToursModalOpen] = useState(false);
  const [hideAllToursDecisionTooltipOpen, setHideAllToursDecisionTooltipOpen] =
    useState(false);
  const hideAllToursDecisionTooltipTimerRef = useRef(null);

  const userId = user?.id;
  const teamId = user?.team_id;

  const resetTourUi = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    onCompleteRef.current = null;
    onCloseRef.current = null;
    currentStepIndexRef.current = 0;
    setTourSessionStateState(getDefaultTourSessionState());
    setHideAllToursModalOpen(false);
    setHideAllToursDecisionTooltipOpen(false);
    if (hideAllToursDecisionTooltipTimerRef.current) {
      clearTimeout(hideAllToursDecisionTooltipTimerRef.current);
      hideAllToursDecisionTooltipTimerRef.current = null;
    }
    lastPromptedSkipCountRef.current = 0;
    dispatch(setRunning(false));
    dispatch(setCurrentTour(null));
    dispatch(
      setTourSettings({
        areToursHidden: false,
        isLoaded: false,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (!userId || !teamId || isImpersonating) {
      resetTourUi();
      return;
    }

    const state = getTourSessionState(userId, teamId);
    setTourSessionStateState(state);
    setHideAllToursModalOpen(false);
    setHideAllToursDecisionTooltipOpen(false);
    if (hideAllToursDecisionTooltipTimerRef.current) {
      clearTimeout(hideAllToursDecisionTooltipTimerRef.current);
      hideAllToursDecisionTooltipTimerRef.current = null;
    }
    lastPromptedSkipCountRef.current = 0;
  }, [userId, teamId, isImpersonating, resetTourUi]);

  useEffect(() => {
    if (!userId || !teamId || isImpersonating) return;
    setTourSessionState(userId, teamId, tourSessionState);
  }, [userId, teamId, isImpersonating, tourSessionState]);

  useEffect(() => {
    if (!isTourSettingsLoaded || isImpersonating) return;

    if (areToursHidden) {
      setHideAllToursModalOpen(false);
      return;
    }

    if (
      !tourSessionState.hideAllToursPrompted &&
      tourSessionState.skipCount >= TOUR_HIDE_PROMPT_SKIP_THRESHOLD &&
      lastPromptedSkipCountRef.current !== tourSessionState.skipCount
    ) {
      lastPromptedSkipCountRef.current = tourSessionState.skipCount;
      setHideAllToursModalOpen(true);
    }
  }, [tourSessionState, areToursHidden, isTourSettingsLoaded, isImpersonating]);

  const dismissHideAllToursDecisionTooltip = useCallback(() => {
    setHideAllToursDecisionTooltipOpen(false);
    if (hideAllToursDecisionTooltipTimerRef.current) {
      clearTimeout(hideAllToursDecisionTooltipTimerRef.current);
      hideAllToursDecisionTooltipTimerRef.current = null;
    }
  }, []);

  const showHideAllToursDecisionTooltip = useCallback(() => {
    setHideAllToursDecisionTooltipOpen(true);
    if (hideAllToursDecisionTooltipTimerRef.current) {
      clearTimeout(hideAllToursDecisionTooltipTimerRef.current);
    }
    hideAllToursDecisionTooltipTimerRef.current = setTimeout(() => {
      setHideAllToursDecisionTooltipOpen(false);
      hideAllToursDecisionTooltipTimerRef.current = null;
    }, 30000);
  }, []);

  const getDriverInstance = useCallback(() => {
    if (!driverRef.current) {
      driverRef.current = driver({
        animate: true,
        showProgress: true,
        showButtons: ['next', 'previous', 'close'],
        allowClose: true,
        allowKeyboardControl: true,
        overlayColor: 'rgba(0, 0, 0, 0.35)',
        stageBackground: 'transparent',
        stagePadding: 12,
        stageRadius: 8,
        popoverOffset: 12,
        progressText: t('tours.progress', {
          current: '{{current}}',
          total: '{{total}}',
        }),
        nextBtnText: t('common.next'),
        prevBtnText: t('common.back'),
        doneBtnText: t('tours.done'),
        onPopoverRender: (popover, { state }) => {
          // Hide the previous button on the first step
          const prevBtn = popover.previousButton;
          if (prevBtn) {
            if (state.activeIndex === 0) {
              prevBtn.style.display = 'none';
            } else {
              prevBtn.style.display = '';
            }
          }
        },
      });
    }
    return driverRef.current;
  }, [t]);

  const recordTourSkip = useCallback(() => {
    if (!userId || !teamId || isImpersonating) return;

    setTourSessionStateState((prev) => {
      const next = {
        ...prev,
        skipCount: prev.skipCount + 1,
      };

      return next;
    });
  }, [userId, teamId, isImpersonating]);

  const stopTour = useCallback(() => {
    if (isImpersonating) return;
    const driverInstance = getDriverInstance();
    driverInstance.destroy();
    dispatch(setRunning(false));
    dispatch(setCurrentTour(null));
  }, [getDriverInstance, dispatch, isImpersonating]);

  const setToursHidden = useCallback(
    async (shouldHide) => {
      if (!userId || !teamId || isImpersonating) return;

      try {
        await tourAPI.updateSettings({ hideAllTours: shouldHide });
        dispatch(
          setTourSettings({
            areToursHidden: shouldHide,
            isLoaded: true,
          })
        );
        mergeTourLocalCache(userId, teamId, {
          settings: {
            areToursHidden: shouldHide,
          },
        });
        setHideAllToursModalOpen(false);
        if (shouldHide) {
          if (isRunning) {
            stopTour();
          }
          setTourSessionStateState((prev) => ({
            ...prev,
            hideAllToursPrompted: true,
          }));
          showHideAllToursDecisionTooltip();
        } else {
          setTourSessionStateState((prev) => ({
            ...prev,
            skipCount: 0,
            hideAllToursPrompted: false,
          }));
          lastPromptedSkipCountRef.current = 0;
          dismissHideAllToursDecisionTooltip();
        }
      } catch (err) {
        dispatch(setError(err.message));
      }
    },
    [
      userId,
      teamId,
      dispatch,
      showHideAllToursDecisionTooltip,
      dismissHideAllToursDecisionTooltip,
      stopTour,
      isImpersonating,
      isRunning,
    ]
  );

  const hideAllTours = useCallback(async () => {
    await setToursHidden(true);
  }, [setToursHidden]);

  const dismissHideAllToursPrompt = useCallback(() => {
    if (isImpersonating) return;
    setHideAllToursModalOpen(false);
    setTourSessionStateState((prev) => ({
      ...prev,
      hideAllToursPrompted: true,
    }));
    showHideAllToursDecisionTooltip();
  }, [showHideAllToursDecisionTooltip, isImpersonating]);

  const clearTourSession = useCallback(() => {
    if (isImpersonating) {
      resetTourUi();
      return;
    }

    if (userId && teamId) {
      clearTourSessionState(userId, teamId);
      clearTourLocalCache(userId, teamId);
    }
    resetTourUi();
  }, [userId, teamId, isImpersonating, resetTourUi]);

  const startTour = useCallback(
    (steps, tourKey, onComplete, startAtStep, onClose) => {
      if (!steps || steps.length === 0) return;

      const discriminators = user ? getDiscriminators(user) : null;

      const filteredSteps = steps.filter((step) => {
        if (step.showIf) {
          return step.showIf(discriminators);
        }
        return true;
      });

      const driverSteps = filteredSteps.map((step) => {
        const stepConfig = {
          popover: {
            title: getBrandText(t(step.title, step.titleValues)),
            description: getBrandText(t(step.content, step.contentValues)),
            progressText: t('tours.progress', {
              current: '{{current}}',
              total: '{{total}}',
            }),
            nextBtnText: t('common.next'),
            prevBtnText: t('common.back'),
            doneBtnText: t('tours.done'),
            side: step.placement || 'bottom',
            align: step.align || 'center',
          },
        };
        // Only add element if target is provided (allows centered modal-style popovers)
        if (step.target) {
          stepConfig.element = step.target;
        }
        return stepConfig;
      });

      if (driverSteps.length === 0) return;

      // Store the onComplete callback
      onCompleteRef.current = onComplete;
      onCloseRef.current = onClose;

      const startIndex = Math.min(
        Math.max(0, startAtStep ?? 0),
        driverSteps.length - 1
      );
      currentStepIndexRef.current = startIndex;

      const driverInstance = getDriverInstance();

      const prepareStepTarget = (stepIndex) => {
        const step = filteredSteps[stepIndex];
        if (!step) return;
        if (step.target && document.querySelector(step.target)) return;
        if (step.autoOpenSelector) {
          const openMenuButton = document.querySelector(step.autoOpenSelector);
          openMenuButton?.dispatchEvent(
            new MouseEvent('click', { bubbles: true, cancelable: true })
          );
        }
      };

      const waitForStepTarget = (stepIndex, callback) => {
        const step = filteredSteps[stepIndex];
        if (!step?.target) {
          callback();
          return;
        }

        const maxAttempts = 20;
        let attempts = 0;

        const checkTarget = () => {
          const targetFound = Boolean(document.querySelector(step.target));
          if (targetFound || attempts >= maxAttempts) {
            callback();
            return;
          }

          attempts += 1;
          setTimeout(checkTarget, 50);
        };

        checkTarget();
      };

      const moveWithPreparedTarget = (stepIndex, direction) => {
        const step = filteredSteps[stepIndex];
        prepareStepTarget(stepIndex);

        const hasReadyTarget =
          !step?.target || Boolean(document.querySelector(step.target));

        if (hasReadyTarget) {
          if (direction === 'next') {
            driverInstance.moveNext();
          } else {
            driverInstance.movePrevious();
          }
          return;
        }

        waitForStepTarget(stepIndex, () => {
          prepareStepTarget(stepIndex);
          requestAnimationFrame(() => {
            if (direction === 'next') {
              driverInstance.moveNext();
            } else {
              driverInstance.movePrevious();
            }
          });
        });
      };

      driverInstance.setConfig({
        steps: driverSteps,
        onNextClick: () => {
          const currentIndex = driverInstance.getActiveIndex();
          const isLastStep = currentIndex === driverSteps.length - 1;
          if (isLastStep) {
            if (onCompleteRef.current) {
              onCompleteRef.current(driverSteps.length);
              onCompleteRef.current = null;
            }
            onCloseRef.current = null;
            driverInstance.destroy();
          } else {
            moveWithPreparedTarget(currentIndex + 1, 'next');
            currentStepIndexRef.current = currentIndex + 1;
          }
        },
        onPrevClick: () => {
          const currentIndex = driverInstance.getActiveIndex();
          if (typeof currentIndex === 'number' && currentIndex > 0) {
            moveWithPreparedTarget(currentIndex - 1, 'prev');
            currentStepIndexRef.current = currentIndex - 1;
          }
        },
        onCloseClick: () => {
          const indexToPersist = currentStepIndexRef.current;
          if (onCloseRef.current && typeof indexToPersist === 'number') {
            onCloseRef.current(indexToPersist);
          }
          recordTourSkip();
          onCloseRef.current = null;
          driverInstance.destroy();
        },
        onDestroyed: () => {
          if (onCloseRef.current) {
            const indexToPersist = currentStepIndexRef.current;
            if (typeof indexToPersist === 'number') {
              onCloseRef.current(indexToPersist);
            }
            recordTourSkip();
            onCloseRef.current = null;
          }
          dispatch(setRunning(false));
          dispatch(setCurrentTour(null));
        },
      });

      dispatch(setCurrentTour(tourKey));
      dispatch(setRunning(true));

      prepareStepTarget(startIndex);

      const startStep = filteredSteps[startIndex];
      const hasReadyTarget =
        !startStep?.target || Boolean(document.querySelector(startStep.target));

      if (hasReadyTarget) {
        driverInstance.drive(startIndex);
      } else {
        waitForStepTarget(startIndex, () => {
          driverInstance.drive(startIndex);
        });
      }
    },
    [user, getDriverInstance, dispatch, recordTourSkip, getBrandText, t]
  );

  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
      if (hideAllToursDecisionTooltipTimerRef.current) {
        clearTimeout(hideAllToursDecisionTooltipTimerRef.current);
        hideAllToursDecisionTooltipTimerRef.current = null;
      }
    };
  }, []);

  const value = {
    startTour,
    stopTour,
    isRunning,
    currentTour,
    areToursHidden,
    setToursHidden,
    hideAllTours,
    hideAllToursModalOpen,
    dismissHideAllToursPrompt,
    hideAllToursDecisionTooltipOpen,
    dismissHideAllToursDecisionTooltip,
    showHideAllToursDecisionTooltip,
    recordTourSkip,
    clearTourSession,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export default TourProvider;
